import { useEffect, useState } from 'react';

/**
 * False until the browser has painted and gone idle.
 *
 * For decoration that must not compete with the content. The banner's team crest
 * is drawn at 240% height, which makes it the largest element on the page and so
 * the thing Largest Contentful Paint measures. Cloudflare put the Resource Load
 * Delay for it at 6.1 seconds of an 8 second LCP: in a client-rendered app the
 * image URL is not even known until the JS has run and the API has answered, and
 * marking it lazy and low priority only pushed it further back.
 *
 * Holding it back until after the first paint lets the headline and the real team
 * logos be what gets measured, and the texture arrives when nothing is waiting on
 * it.
 */
export function useAfterPaint(): boolean {
  const [painted, setPainted] = useState(false);

  useEffect(() => {
    let id: number | undefined;
    // Safari has no requestIdleCallback, so fall back to a short timer
    const hasIdle = typeof window.requestIdleCallback === 'function';
    // two frames, so the first paint has definitely happened, then idle
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        id = hasIdle
          ? window.requestIdleCallback(() => setPainted(true), { timeout: 2000 })
          : window.setTimeout(() => setPainted(true), 200);
      }),
    );
    return () => {
      cancelAnimationFrame(raf);
      if (id === undefined) return;
      // Do not assume the pair exists together. Cancelling is only an optimisation,
      // and throwing here would surface as an error on unmount.
      if (hasIdle && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(id);
      else if (!hasIdle) window.clearTimeout(id);
    };
  }, []);

  return painted;
}
